import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, HeartHandshake, Megaphone, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ways = [
  {
    icon: Building2,
    title: "Government & Philanthropy",
    body: "Co-design and co-fund community-led food resilience programs across remote Australia.",
  },
  {
    icon: HeartHandshake,
    title: "Corporate Partners",
    body: "Long-term partnerships in supply chain, logistics, infrastructure and skilled-volunteer programs.",
  },
  {
    icon: Users,
    title: "Communities",
    body: "Talk to us about bringing Growing Country to your community — on your terms, at your pace.",
  },
  {
    icon: Megaphone,
    title: "Supporters",
    body: "Stay close to the work. Subscribe to our briefings and share what we're building.",
  },
];

export default function GetInvolved() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're in. Briefings will land in your inbox.");
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="relative bg-stone-alt py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-rust mb-6">
              Get Involved
            </div>
            <h2
              data-testid="get-involved-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-charcoal"
            >
              This is a long-term commitment.
              <span className="block italic text-rust">
                It will take all of us.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base md:text-lg leading-relaxed text-ink">
              Growing Country is designed to complement existing food systems —
              and to be built in partnership with the communities, governments,
              and organisations who share the vision.
            </p>
          </div>
        </div>

        {/* Ways to engage */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-line border border-stone-line">
          {ways.map((w, i) => {
            const Icon = w.icon;
            return (
              <div
                key={w.title}
                data-testid={`way-${i}`}
                className="bg-sand p-8 md:p-10 group hover:bg-charcoal hover:text-sand transition-colors duration-500"
              >
                <Icon
                  strokeWidth={1.5}
                  className="w-7 h-7 text-rust group-hover:text-ochre transition-colors"
                />
                <h3 className="mt-8 font-serif text-2xl leading-tight">
                  {w.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink group-hover:text-sand/80 transition-colors">
                  {w.body}
                </p>
                <a
                  href="#contact"
                  data-testid={`way-cta-${i}`}
                  className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-rust group-hover:text-ochre"
                >
                  <span className="text-link">Start a conversation</span>
                  <span aria-hidden>→</span>
                </a>
              </div>
            );
          })}
        </div>

        {/* Newsletter */}
        <div className="mt-20 md:mt-28 bg-charcoal text-sand relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-50" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 p-10 md:p-16 items-center">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-ochre mb-5">
                The Field Briefing
              </div>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
                Quarterly updates from the ground.
              </h3>
              <p className="mt-5 max-w-lg text-sand/80 leading-relaxed">
                Field stories, harvest reports, pilot outcomes and partnership
                opportunities. No noise. We respect your inbox.
              </p>
            </div>

            <form
              onSubmit={onSubscribe}
              data-testid="newsletter-form"
              className="md:col-span-5"
            >
              <label
                htmlFor="newsletter-email"
                className="block text-[11px] uppercase tracking-[0.28em] text-sand/60 mb-3"
              >
                Email address
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent border-b border-sand/40 focus:border-ochre px-0 py-3 outline-none text-sand placeholder:text-sand/40 transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="inline-flex items-center justify-center gap-2 bg-rust text-sand px-7 py-3 text-xs uppercase tracking-[0.25em] hover:bg-rust-dark transition-colors disabled:opacity-60"
                >
                  {loading ? "Subscribing…" : "Subscribe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
